import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { IoTrashOutline, IoPersonAddOutline, IoBanOutline, IoCheckmarkCircle, IoInfiniteOutline, IoRefreshOutline, IoJournalOutline } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { adminListMembers, adminUpdateMember, adminDeleteMember, adminListGrants, adminAddGrants, adminDeleteGrant, adminCreateMember, adminGrantCoaching } from "@/lib/api";

const ACCESS_LABELS = { lifetime: "Accès à vie", subscription: "Abonné", granted: "Offert" };

export default function AdminMembres() {
  const { isAdmin, loading } = useAuth();
  const nav = useNavigate();
  const [members, setMembers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [emails, setEmails] = useState("");
  const [grantType, setGrantType] = useState("subscriber");
  const [q, setQ] = useState("");
  const [nm, setNm] = useState({ email: "", password: "", name: "", access_type: "subscription" });

  useEffect(() => { if (!loading && isAdmin === false) nav("/hub", { replace: true }); }, [loading, isAdmin, nav]);

  const load = async () => {
    setLoadingData(true);
    try {
      const [m, g] = await Promise.all([adminListMembers(), adminListGrants()]);
      setMembers(m); setGrants(g);
    } catch { toast.error("Chargement impossible"); } finally { setLoadingData(false); }
  };
  useEffect(() => { if (!loading && isAdmin) load(); }, [loading, isAdmin]);

  const patch = async (id, body, okMsg) => {
    try { const updated = await adminUpdateMember(id, body); setMembers((ms) => ms.map((m) => (m.id === id ? updated : m))); if (okMsg) toast.success(okMsg); }
    catch { toast.error("Échec de la mise à jour"); }
  };
  const removeMember = async (m) => {
    if (!window.confirm(`Supprimer le compte de ${m.name || m.email} ? Cette action est définitive.`)) return;
    try { await adminDeleteMember(m.id); setMembers((ms) => ms.filter((x) => x.id !== m.id)); toast.success("Compte supprimé"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  const addGrants = async () => {
    const list = emails.split(/[\n,; ]+/).map((x) => x.trim()).filter((x) => x.includes("@"));
    if (!list.length) { toast.error("Aucun email valide"); return; }
    try { const r = await adminAddGrants(list, grantType, ""); toast.success(`${r.added} accès ajouté(s)`); setEmails(""); load(); }
    catch { toast.error("Échec de l'ajout"); }
  };
  const removeGrant = async (email) => {
    try { await adminDeleteGrant(email); setGrants((gs) => gs.filter((g) => g.email !== email)); }
    catch { toast.error("Échec"); }
  };
  const createMember = async () => {
    if (!nm.email.includes("@") || nm.password.length < 6) { toast.error("Email valide + mot de passe (6+ caractères) requis"); return; }
    try {
      const m = await adminCreateMember(nm);
      setMembers((ms) => [m, ...ms]);
      setNm({ email: "", password: "", name: "", access_type: "subscription" });
      toast.success("Compte membre créé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
  };
  const grantCoaching = async (id, delta) => {
    try {
      const updated = await adminGrantCoaching(id, delta);
      setMembers((ms) => ms.map((m) => (m.id === id ? updated : m)));
      toast.success(delta > 0 ? "1 séance Renzoku offerte 🎁" : "Crédit retiré");
    } catch { toast.error("Échec de l'opération"); }
  };

  const filtered = members.filter((m) => {
    const t = q.toLowerCase();
    return !t || (m.email || "").toLowerCase().includes(t) || (m.name || "").toLowerCase().includes(t);
  });
  const stats = {
    total: members.length,
    active: members.filter((m) => m.is_subscriber && !m.access_suspended).length,
    lifetime: members.filter((m) => m.access_type === "lifetime").length,
    suspended: members.filter((m) => m.access_suspended).length,
  };

  return (
    <DojoShell crumbs={[{ label: "Membres & Accès" }]}>
      <div style={s.head}>
        <div>
          <h1 style={s.h1}>Gestion des membres</h1>
          <p style={s.sub}>Abonnés, accès offerts et accès à vie — tout se pilote ici.</p>
        </div>
        <button style={s.refresh} onClick={load} data-testid="admin-refresh"><IoRefreshOutline size={16} /> Actualiser</button>
      </div>

      <div style={s.statsRow}>
        <Stat label="Membres" value={stats.total} />
        <Stat label="Accès actifs" value={stats.active} color="#1a7f37" />
        <Stat label="Accès à vie" value={stats.lifetime} color={RED} />
        <Stat label="Suspendus" value={stats.suspended} color="#b26a00" />
      </div>

      {/* Accès offerts */}
      <div style={s.card} data-testid="admin-grants-card">
        <h2 style={s.cardTitle}><IoPersonAddOutline size={18} /> Offrir un accès (anciens abonnés / accès à vie)</h2>
        <p style={s.cardHint}>Colle une liste d'emails (séparés par des virgules, espaces ou retours à la ligne). Ces personnes pourront créer leur compte sans payer.</p>
        <textarea style={s.textarea} rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="marie@exemple.com, jean@exemple.com…" data-testid="admin-grants-input" />
        <div style={s.grantRow}>
          <select style={s.select} value={grantType} onChange={(e) => setGrantType(e.target.value)} data-testid="admin-grant-type">
            <option value="subscriber">Abonné (accès complet)</option>
            <option value="lifetime">Accès à vie (permanent)</option>
          </select>
          <button style={s.addBtn} onClick={addGrants} data-testid="admin-grants-add">Ajouter les accès</button>
        </div>
        {grants.length > 0 && (
          <div style={s.grantList} data-testid="admin-grants-list">
            {grants.map((g) => (
              <span key={g.email} style={s.grantChip}>
                {g.type === "lifetime" ? "♾️" : "★"} {g.email}
                <button style={s.chipX} onClick={() => removeGrant(g.email)} title="Retirer">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Créer un compte membre directement */}
      <div style={s.card} data-testid="admin-create-card">
        <h2 style={s.cardTitle}><IoPersonAddOutline size={18} /> Créer un compte membre</h2>
        <p style={s.cardHint}>Crée directement un accès (email + mot de passe) sans paiement. Idéal pour un ancien membre : tu lui transmets ses identifiants.</p>
        <div style={s.createGrid}>
          <input style={s.cInput} placeholder="Nom" value={nm.name} onChange={(e) => setNm({ ...nm, name: e.target.value })} data-testid="create-member-name" />
          <input style={s.cInput} type="email" placeholder="Email" value={nm.email} onChange={(e) => setNm({ ...nm, email: e.target.value })} data-testid="create-member-email" />
          <input style={s.cInput} type="text" placeholder="Mot de passe (6+)" value={nm.password} onChange={(e) => setNm({ ...nm, password: e.target.value })} data-testid="create-member-password" />
          <select style={s.select} value={nm.access_type} onChange={(e) => setNm({ ...nm, access_type: e.target.value })} data-testid="create-member-type">
            <option value="subscription">Abonné</option>
            <option value="lifetime">Accès à vie</option>
          </select>
          <button style={s.addBtn} onClick={createMember} data-testid="create-member-submit">Créer le compte</button>
        </div>
      </div>

      {/* Liste des membres */}
      <div style={s.card}>
        <div style={s.membersHead}>
          <h2 style={s.cardTitle}>Tous les membres</h2>
          <input style={s.search} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un email / nom…" data-testid="admin-member-search" />
        </div>
        {loadingData ? <p style={s.muted}>Chargement…</p> : (
          <div style={s.tableWrap}>
            <table style={s.table} data-testid="admin-members-table">
              <thead>
                <tr>
                  <th style={s.th}>Membre</th><th style={s.th}>Accès</th><th style={s.th}>Coaching</th><th style={s.thR}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id} style={s.tr} data-testid={`admin-member-${m.id}`}>
                    <td style={s.td}>
                      <div style={s.mName}>{m.name || "—"} {m.role === "admin" && <span style={s.adminTag}>Admin</span>}</div>
                      <div style={s.mEmail}>{m.email} · {m.provider}</div>
                    </td>
                    <td style={s.td}>
                      {m.access_suspended ? <span style={{ ...s.badge, background: "#fff3e0", color: "#b26a00" }}>Suspendu</span>
                        : m.is_subscriber ? <span style={{ ...s.badge, background: "#e8f7ec", color: "#1a7f37" }}>{ACCESS_LABELS[m.access_type] || "Actif"}</span>
                        : <span style={{ ...s.badge, background: "#f3f3f3", color: "#888" }}>Sans accès</span>}
                    </td>
                    <td style={s.td}>
                      <div style={s.coachCell}>
                        <span style={s.credits}>{m.coaching_credits} Renzoku</span>
                        <div style={s.coachBtns}>
                          <button style={s.coachBtn} title="Offrir 1 séance Renzoku" onClick={() => grantCoaching(m.id, 1)} data-testid={`member-renzoku-add-${m.id}`}>+</button>
                          {m.coaching_credits > 0 && (
                            <button style={{ ...s.coachBtn, color: RED }} title="Retirer 1 séance" onClick={() => grantCoaching(m.id, -1)} data-testid={`member-renzoku-remove-${m.id}`}>−</button>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={s.tdR}>
                      {m.role !== "admin" && (
                        <div style={s.actions}>
                          <button style={{ ...s.iconBtn, color: "var(--brand)" }} title="Ouvrir le carnet" onClick={() => nav(`/carnet/eleves/${m.id}`)} data-testid={`member-carnet-${m.id}`}><IoJournalOutline size={17} /></button>
                          {m.access_type === "lifetime"
                            ? <button style={s.iconBtn} title="Passer en abonné" onClick={() => patch(m.id, { access_type: "subscription" }, "Mis en abonné")} data-testid={`member-tosub-${m.id}`}><IoCheckmarkCircle size={17} /></button>
                            : <button style={s.iconBtn} title="Donner un accès à vie" onClick={() => patch(m.id, { is_subscriber: true, access_type: "lifetime", access_suspended: false }, "Accès à vie accordé")} data-testid={`member-lifetime-${m.id}`}><IoInfiniteOutline size={17} /></button>}
                          <button style={{ ...s.iconBtn, color: m.access_suspended ? "#1a7f37" : "#b26a00" }} title={m.access_suspended ? "Réactiver" : "Suspendre"} onClick={() => patch(m.id, { access_suspended: !m.access_suspended }, m.access_suspended ? "Accès réactivé" : "Accès suspendu")} data-testid={`member-suspend-${m.id}`}>
                            {m.access_suspended ? <IoCheckmarkCircle size={17} /> : <IoBanOutline size={17} />}
                          </button>
                          <button style={{ ...s.iconBtn, color: RED }} title="Supprimer" onClick={() => removeMember(m)} data-testid={`member-delete-${m.id}`}><IoTrashOutline size={16} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DojoShell>
  );
}

const Stat = ({ label, value, color }) => (
  <div style={s.stat}><div style={{ ...s.statVal, color: color || "var(--text-primary)" }}>{value}</div><div style={s.statLbl}>{label}</div></div>
);

const s = {
  head: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18, flexWrap: "wrap" },
  h1: { fontSize: 30, fontWeight: 900, color: "var(--text-primary)", fontFamily: "var(--font-display)", margin: 0 },
  sub: { color: "var(--text-secondary)", fontSize: 14, marginTop: 4 },
  refresh: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13, cursor: "pointer" },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 12, marginBottom: 22 },
  stat: { padding: "16px 18px", borderRadius: 16, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  statVal: { fontSize: 28, fontWeight: 900, fontFamily: "var(--font-display)" },
  statLbl: { fontSize: 12.5, color: "var(--text-secondary)", marginTop: 2 },
  card: { padding: "22px 22px", borderRadius: 18, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)", marginBottom: 20 },
  cardTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 17, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 8px" },
  cardHint: { fontSize: 13, color: "var(--text-secondary)", margin: "0 0 12px", lineHeight: 1.5 },
  textarea: { width: "100%", padding: "11px 13px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, fontFamily: "inherit", color: "var(--text-primary)", background: "var(--bg-tertiary)", boxSizing: "border-box", resize: "vertical" },
  grantRow: { display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" },
  select: { padding: "10px 12px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, color: "var(--text-primary)", background: "var(--bg-tertiary)" },
  addBtn: { padding: "10px 18px", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" },
  createGrid: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  cInput: { flex: "1 1 160px", minWidth: 140, padding: "10px 12px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, color: "var(--text-primary)", background: "var(--bg-tertiary)" },
  grantList: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 },
  grantChip: { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 999, background: "var(--bg-tertiary)", border: "1px solid var(--border-c)", fontSize: 12.5, color: "var(--text-primary)" },
  chipX: { border: "none", background: "transparent", color: RED, fontWeight: 900, cursor: "pointer", fontSize: 15, lineHeight: 1 },
  membersHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" },
  search: { padding: "9px 13px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 13.5, color: "var(--text-primary)", background: "var(--bg-tertiary)", minWidth: 220 },
  muted: { color: "var(--text-secondary)", fontSize: 14 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13.5 },
  th: { textAlign: "left", padding: "8px 10px", color: "var(--text-secondary)", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid var(--border-c)" },
  thR: { textAlign: "right", padding: "8px 10px", color: "var(--text-secondary)", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid var(--border-c)" },
  tr: { borderBottom: "1px solid var(--border-c)" },
  td: { padding: "12px 10px", color: "var(--text-primary)", verticalAlign: "middle" },
  tdR: { padding: "12px 10px", textAlign: "right", verticalAlign: "middle" },
  mName: { fontWeight: 700, display: "flex", alignItems: "center", gap: 8 },
  adminTag: { fontSize: 10.5, fontWeight: 800, color: RED, background: `${RED}18`, padding: "2px 7px", borderRadius: 999 },
  mEmail: { fontSize: 12, color: "var(--text-secondary)", marginTop: 2 },
  badge: { display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700 },
  credits: { fontSize: 13, color: "var(--text-secondary)" },
  coachCell: { display: "flex", alignItems: "center", gap: 8 },
  coachBtns: { display: "inline-flex", gap: 4 },
  coachBtn: { width: 26, height: 26, borderRadius: 7, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", cursor: "pointer", fontWeight: 900, fontSize: 16, lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center" },
  actions: { display: "inline-flex", gap: 6, justifyContent: "flex-end" },
  iconBtn: { width: 34, height: 34, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", cursor: "pointer" },
};
